import { Typography } from '@material-ui/core'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import IndeterminateCheckBoxIcon from '@material-ui/icons/IndeterminateCheckBox'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { formatNumber } from 'src/commons/helpers'
import { SidePanelSection } from 'src/components/SidePanel'
import theme from 'src/theme'

import { FilterStateEnum, IToggleState } from '../SidePanelSection'

export interface IEntry {
  additionalComponents?: React.ReactNode
  description?: string
  id: string
  label: string
  value?: number
}

export interface IProps {
  checkedSet: Set<string>
  disabled?: boolean
  entries: IEntry[]
  expanded: boolean
  formatValue?: boolean
  onDeselectAll?: () => void
  onSelectAll?: (keys: string[]) => void
  onToggleCheck: (checked: string) => void
  onToggleExpanded: (expanded: boolean) => void
  overwriteSelectAllWith?: string
  showSelectAll?: boolean
  title?: string
}

/* Template section with a checkbox selector */
const CheckboxesSection = (props: IProps) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const {
    title,
    expanded,
    onToggleExpanded,
    entries,
    checkedSet,
    onToggleCheck,
    onSelectAll,
    onDeselectAll,
    disabled,
    formatValue,
  } = props

  const areAllEntriesSelected = entries.every((entry) => checkedSet.has(entry.id))
  const someEntriesSelected = entries.some((entry) => checkedSet.has(entry.id))
  const areAllEntriesUnselected = !someEntriesSelected
  const areAllEntriesImplicitlySelected = areAllEntriesSelected || areAllEntriesUnselected

  const filterState: IToggleState = useMemo(() => {
    if (areAllEntriesSelected) {
      return {
        state: FilterStateEnum.FULL,
        label: t('filters.checkbox.allSelected'),
      }
    }

    if (someEntriesSelected) {
      return {
        state: FilterStateEnum.PARTIAL,
        label: t('filters.checkbox.someSelected', { size: checkedSet.size, length: entries.length }),
      }
    }

    return {
      state: FilterStateEnum.NONE,
      label: t('filters.checkbox.nothingSelected'),
    }
  }, [areAllEntriesSelected, someEntriesSelected, checkedSet, entries])

  return (
    <SidePanelSection
      title={title}
      expandable={true}
      expanded={expanded}
      onToggleExpanded={onToggleExpanded}
      filterState={filterState?.state ?? undefined}
      subtitle={filterState.label}
      onSelectAll={() => onSelectAll?.(entries.map((e) => e.id))}
      onDeselectAll={() => onDeselectAll?.()}
    >
      <List dense={true} className={classes.list}>
        {entries.map(({ id, label, value, additionalComponents, description }) => {
          const isEntrySelected = checkedSet.has(id)
          const shouldDisplayValue = areAllEntriesImplicitlySelected || isEntrySelected

          let displayedValue = ''
          if (shouldDisplayValue) {
            const formattedValue = formatValue ? formatNumber(value) : value
            if (formattedValue !== undefined && typeof formattedValue === 'number') {
              displayedValue = formattedValue.toString()
            } else if (formattedValue !== undefined) {
              displayedValue = formattedValue
            }
          }

          return (
            <ListItem key={id} data-entry-id={id} data-testid={id} dense disableGutters className={classes.listItem}>
              <FormControlLabel
                classes={{
                  root: classes.checkboxAndLabel,
                  label: classes.label,
                }}
                control={
                  <Checkbox
                    icon={<CustomBlankCheckboxIcon />}
                    checkedIcon={<CustomCheckboxIcon />}
                    className={classes.checkbox}
                    onClick={() => onToggleCheck(id)}
                    checked={isEntrySelected}
                    color="primary"
                    disabled={disabled}
                  />
                }
                label={label}
                title={description || label}
              />
              {additionalComponents !== undefined && additionalComponents}
              <Typography className={classes.value}>{displayedValue}</Typography>
            </ListItem>
          )
        })}
      </List>
    </SidePanelSection>
  )
}

export const CustomCheckboxIcon = withStyles({
  root: {},
})(CheckBoxIcon)

export const CustomDeleteCheckboxIcon = withStyles({
  root: {
    color: theme.palette.error.main,
  },
})(IndeterminateCheckBoxIcon)

export const CustomBlankCheckboxIcon = withStyles({
  root: {},
})(CheckBoxOutlineBlankIcon)

const useStyles = makeStyles(() => ({
  list: { flex: 1, overflow: 'hidden', width: '100%', padding: 0 },
  checkboxAndLabel: {
    width: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    margin: '0px',
  },
  label: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    fontSize: 13,
    fontWeight: 'normal',
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: 3.08,
    letterSpacing: 'normal',
    color: '#192233',
  },
  boldLabel: {
    fontWeight: 'bold',
  },
  checkbox: {
    padding: 0,
    paddingRight: 10,
  },
  listItem: {
    height: 30,
    paddingTop: 0,
    paddingBottom: 0,
  },
  value: {
    fontSize: 13,
    lineHeight: 3.08,
    textAlign: 'right',
    color: '#192233',
    minWidth: 50,
  },
}))

export default CheckboxesSection
